import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PlacePicker from "@/components/PlacePicker";
import ColorPickerField from "@/components/ColorPickerField";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PencilSimple, User as UserIcon, Buildings } from "@phosphor-icons/react";

/** Inline client editor — name, phone, email, and address (with PlacePicker). Admin only. */
export default function EditClientDialog({ open, onOpenChange, client, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!client) return;
    setForm({
      name: client.name || "",
      phone: client.phone || "",
      email: client.email || "",
      address: client.address || "",
      postal_code: client.postal_code || "",
      city: client.city || "",
      latitude: client.latitude ?? null,
      longitude: client.longitude ?? null,
      type: client.type || "particulier",
      color: client.color || null,
      siret: client.siret || "",
      company_name: client.company_name || "",
    });
  }, [client]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPickAddress = (p) => {
    setForm((f) => ({
      ...f,
      address: p.address || p.label || "",
      postal_code: p.postal_code || null,
      city: p.city || null,
      latitude: p.latitude ?? null,
      longitude: p.longitude ?? null,
    }));
  };

  const save = async () => {
    if (!form.name || !form.name.trim()) { toast.error("Le nom du client est requis"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      // Empty strings → null for optional fields the backend prefers null
      ["phone", "email", "address", "postal_code", "city", "siret", "company_name"].forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      await api.patch(`/clients/${client.id}`, payload);
      toast.success("Client mis à jour");
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (!client) return null;
  const isPro = form.type === "pro";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#111] border-white/10 text-white rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-client-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <PencilSimple size={20} className="text-[#005CFF]" weight="duotone" /> Modifier le client
          </DialogTitle>
          <DialogDescription className="text-[10px] uppercase tracking-widest text-white/50 mt-0.5">
            {client.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {/* Type toggle */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { v: "particulier", label: "Particulier", Icon: UserIcon, color: "#10B981" },
              { v: "pro", label: "Professionnel", Icon: Buildings, color: "#8B5CF6" },
            ].map((o) => {
              const on = form.type === o.v;
              return (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => set("type", o.v)}
                  className={`h-10 rounded-xl border flex items-center justify-center gap-1.5 text-xs font-semibold transition-all ${on ? "text-white" : "bg-white/5 border-white/10 text-white/60"}`}
                  style={on ? { background: o.color + "22", borderColor: o.color, color: "#fff" } : {}}
                  data-testid={`edit-cl-type-${o.v}`}
                >
                  <o.Icon size={13} weight={on ? "fill" : "duotone"} style={on ? { color: o.color } : {}} />
                  {o.label}
                </button>
              );
            })}
          </div>

          {/* Pro-only fields */}
          {isPro && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-white/60 text-[10px] uppercase tracking-widest">SIRET</Label>
                <Input value={form.siret || ""} onChange={(e) => set("siret", e.target.value)} className="bg-white/5 border-white/10 h-10 mt-1" data-testid="edit-cl-siret" />
              </div>
              <div>
                <Label className="text-white/60 text-[10px] uppercase tracking-widest">Raison sociale</Label>
                <Input value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} className="bg-white/5 border-white/10 h-10 mt-1" data-testid="edit-cl-company" />
              </div>
            </div>
          )}

          <div>
            <Label className="text-white/60 text-[10px] uppercase tracking-widest">Nom *</Label>
            <Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} className="bg-white/5 border-white/10 h-10 mt-1" data-testid="edit-cl-name" />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-white/60 text-[10px] uppercase tracking-widest">Téléphone</Label>
              <Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className="bg-white/5 border-white/10 h-10 mt-1" data-testid="edit-cl-phone" />
            </div>
            <div>
              <Label className="text-white/60 text-[10px] uppercase tracking-widest">Email</Label>
              <Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} className="bg-white/5 border-white/10 h-10 mt-1" data-testid="edit-cl-email" />
            </div>
          </div>

          <div>
            <Label className="text-white/60 text-[10px] uppercase tracking-widest">Adresse</Label>
            <div className="mt-1">
              <PlacePicker
                value={form.address}
                onChange={(v) => set("address", v)}
                onPick={onPickAddress}
                placeholder="Rechercher une adresse…"
                testid="edit-cl-address-picker"
              />
            </div>
            {form.postal_code && form.city && (
              <div className="text-[9px] text-emerald-400 mt-1">
                📍 {form.postal_code} {form.city}
                {form.latitude != null && ` · ${form.latitude.toFixed(4)}, ${form.longitude.toFixed(4)}`}
              </div>
            )}
          </div>

          <ColorPickerField
            value={form.color}
            onChange={(v) => set("color", v)}
            testid="edit-cl-color"
            label="Couleur du client"
          />
        </div>

        <div className="flex gap-2 mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="flex-1 border border-white/10 hover:bg-white/5">Annuler</Button>
          <Button onClick={save} disabled={saving} className="flex-1 bg-[#005CFF] hover:bg-[#0047CC]" data-testid="edit-cl-save">
            {saving ? "…" : "Enregistrer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
